import {create, type StateCreator} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';
import {spawn, type ModuleThread} from 'threads';
import {typedEntries} from '@netx/core-next/utils/object';
import {refreshJwts} from '@netx/core-auth-next/api/refreshJwts';
import {type IAsset} from '../../../schema';
import type {AssetJwtItemMapIndex, AssetJwtType} from '../types';
import {getAssetJwtMapIndex} from '../utils';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import window from 'global/window';

// TODO: cleanup and document

const logger = getPrefixedLogger('assetJwtStore');

let worker: ModuleThread<any> | null = null;

type AssetJwtState = {
	items: Record<AssetJwtItemMapIndex, string>;
	usageCount: Record<AssetJwtItemMapIndex, number>;
	add: (assets: Pick<IAsset, 'jwt' | 'assetId'>[], type?: AssetJwtType) => void;
	remove: (assetIds: number[], type?: AssetJwtType) => void;
	update: (
		items: Record<AssetJwtItemMapIndex, string>,
		remove?: boolean,
	) => void;
};

const getAssetJwtDataFromAssets = (
	assets: Pick<IAsset, 'jwt' | 'assetId'>[],
): [number, string][] => {
	return assets
		.filter((asset) => asset.jwt)
		.map<[number, string]>(({assetId, jwt}) => [assetId, jwt as string]);
};

const getItemDataFromAssetJwtData = (
	assetJwtData: [number, string][],
	type: AssetJwtType,
	usageCount?: Record<AssetJwtItemMapIndex, number>,
): Record<AssetJwtItemMapIndex, string> => {
	usageCount = usageCount ?? {};
	return Object.fromEntries(
		assetJwtData.map(([assetId, jwt]) => {
			const mapIndex = getAssetJwtMapIndex(assetId, type);
			usageCount[mapIndex] = (usageCount[mapIndex] || 0) + 1;
			logger.debug(
				'adding asset >',
				assetId,
				'> instances >',
				usageCount[mapIndex],
			);
			return [mapIndex, jwt];
		}),
	);
};

const assetJwtStateCreator: StateCreator<AssetJwtState, [], []> = (
	set,
	get,
) => ({
	items: {},
	usageCount: {},
	add: (assets, type = 'proxy') => {
		const data = getAssetJwtDataFromAssets(assets);
		worker?.add(data, type);
		set((state) => {
			const usageCount = {...state.usageCount};
			const newItemData = {
				...state.items,
				...getItemDataFromAssetJwtData(data, type, usageCount),
			};
			logger.debug('added >', {...newItemData}, '> usageCount >', {
				...usageCount,
			});
			return {
				items: {...state.items, ...newItemData},
				usageCount,
			};
		});
	},
	remove: (assetIds, type = 'proxy') => {
		worker?.remove(assetIds, type);
		set((state) => {
			const usageCount = {...state.usageCount};
			const newItems = {...state.items};
			assetIds.forEach((assetId) => {
				const mapIndex = getAssetJwtMapIndex(assetId, type);
				logger.debug('removing item >', mapIndex);

				if (state.items[mapIndex] == null) {
					logger.warn('item not found in store >', mapIndex);
					return;
				}

				usageCount[mapIndex] = Math.max((usageCount[mapIndex] || 1) - 1, 0);
				logger.debug(
					'removed item >',
					mapIndex,
					'> instances left >',
					usageCount[mapIndex],
				);
				if (usageCount[mapIndex] <= 0) {
					logger.debug(
						'no more instances exist, removing from store >',
						mapIndex,
					);
					delete usageCount[mapIndex];
					delete newItems[mapIndex];
				}
			});
			logger.debug('after remove >', {...newItems}, '> usageCount >', {
				...usageCount,
			});
			return {items: newItems, usageCount};
		});
	},
	update: (items: Record<AssetJwtItemMapIndex, string>, remove = false) => {
		set((state) => {
			const newItems = {...state.items};
			const usageCount = {...state.usageCount};
			logger.debug('update >', items, `(should remove: ${remove.toString()})`);
			remove &&
				typedEntries(state.items).forEach(([key, item]) => {
					if (!items[key]) {
						delete newItems[key];
						delete usageCount[key];
						const keyParts = key.split('-');
						logger.debug(
							'update > removing asset >',
							keyParts[0],
							'> type >',
							keyParts[1],
						);
						worker?.remove(
							[parseInt(keyParts[0], 10)],
							keyParts[1] as AssetJwtType,
						);
					}
				});
			typedEntries(items).forEach(([key, item]) => {
				newItems[key] = item;
				if (!state.items[key]) usageCount[key] = 1;
				const keyParts = key.split('-');
				worker?.add(
					[[parseInt(keyParts[0], 10), item]],
					keyParts[1] as AssetJwtType,
				);
			});
			logger.debug('after update >', {...newItems}, '> usageCount >', {
				...usageCount,
			});
			return {items: newItems, usageCount};
		});
	},
});

const createAssetJwtStore = () =>
	create<AssetJwtState>()(subscribeWithSelector(assetJwtStateCreator));

export const assetJwtStore = createAssetJwtStore();

window.assetJwtStore = assetJwtStore;

if (process.env.NODE_ENV !== 'test') {
	spawn(
		new Worker(new URL('../workers/assetJwtTracker.ts', import.meta.url)),
	).then((_worker) => {
		worker = _worker;
		worker
			.toRefresh()
			.subscribe(async (tokens: [AssetJwtItemMapIndex, string][]) => {
				const newJwts = await refreshJwts({
					jwts: tokens.map(([_, jwt]) => jwt),
				});
				const items: Record<AssetJwtItemMapIndex, string> = {};
				tokens.forEach(([mapIndex], i) => {
					items[mapIndex] = newJwts[i];
				});
				assetJwtStore.getState().update(items, false);
			});
	});
}
